import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef } from 'react';
import { type Control, Controller, useForm, useWatch } from 'react-hook-form';

import { mensagemDeErro } from '@/lib/erros';
import { ApiError } from '@/lib/http';
import { CampoListaChips } from '@/ui/components/campo-lista-chips';
import { Segmentado } from '@/ui/components/segmentado';
import { toast } from '@/ui/primitives/aviso';
import { Button } from '@/ui/primitives/button';
import { Campo, Input } from '@/ui/primitives/input';
import { Interruptor } from '@/ui/primitives/interruptor';
import {
  type CfopEntradaPersonalizada,
  type ClassificacaoPersonalizadaEntrada,
  classificacaoPersonalizadaEntradaSchema,
} from '@fiscoai/contracts';

import { interpretarCfop } from '../regras/textos';

import { useSalvarClassificacao } from './api';
import { type FormClassificacao, type RascunhoClassificacao } from './formulario-classificacao';
import { FraseClassificacao } from './frase-classificacao';
import { GRUPO, GRUPOS, limparFinal } from './textos';

interface Props {
  empresaId: string;
  rascunho: RascunhoClassificacao;
  aoFechar: () => void;
}

type Form = ReturnType<
  typeof useForm<FormClassificacao, unknown, ClassificacaoPersonalizadaEntrada>
>;

/** Formulário e gravação; nome repetido (409) volta como erro do campo nome. */
function useEditor({ empresaId, rascunho, aoFechar }: Props) {
  const form = useForm<FormClassificacao, unknown, ClassificacaoPersonalizadaEntrada>({
    resolver: zodResolver(classificacaoPersonalizadaEntradaSchema),
    defaultValues: rascunho.valores,
  });
  const salvar = useSalvarClassificacao(empresaId);
  const { setFocus } = form;
  useEffect(() => {
    setFocus('nome');
  }, [setFocus]);
  const enviar = form.handleSubmit(async (dados) => {
    try {
      await salvar.mutateAsync({ ...dados, id: rascunho.id });
      toast.success('Classificação salva');
      aoFechar();
    } catch (erro) {
      if (erro instanceof ApiError && erro.status === 409)
        form.setError('nome', { message: mensagemDeErro(erro) });
      else toast.error(mensagemDeErro(erro));
    }
  });
  return { form, salvando: salvar.isPending, enviar };
}

/** O rótulo que se vê; o nome acessível já está no grupo de opções. */
function RotuloVisual({ children }: { children: string }) {
  return (
    <span aria-hidden className="text-sm font-medium">
      {children}
    </span>
  );
}

const MODOS_CFOP = [
  { valor: 'FINAL', rotulo: 'Final fixo', descricao: 'Ex.: x949 para os CFOPs que você listar' },
  { valor: 'MESMO', rotulo: 'O mesmo do fornecedor', descricao: 'Copia o final: 5102 vira x102' },
] as const;

/** Final de 3 dígitos com o "x" à frente (o 1º dígito vem da UF) e os CFOPs do fornecedor. */
function CamposFinal({ control }: { control: Control<FormClassificacao> }) {
  return (
    <div className="grid gap-3 sm:grid-cols-[auto_1fr]">
      <Controller
        control={control}
        name="cfopEntrada.final"
        render={({ field, fieldState }) => (
          <Campo
            rotulo="Final do CFOP"
            erro={fieldState.error ? 'Três dígitos, ex.: 949' : undefined}
          >
            {(a) => (
              <span className="relative block sm:w-28">
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-y-0 left-2.5 grid place-items-center font-mono text-texto-fraco"
                >
                  x
                </span>
                <Input
                  {...a}
                  inputMode="numeric"
                  maxLength={4}
                  placeholder="949"
                  className="pl-5 font-mono tabular"
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(limparFinal(e.target.value));
                  }}
                  onBlur={field.onBlur}
                />
              </span>
            )}
          </Campo>
        )}
      />
      <Controller
        control={control}
        name="cfopEntrada.cfopsFornecedor"
        render={({ field, fieldState }) => (
          <Campo
            rotulo="Para os CFOPs do fornecedor"
            erro={fieldState.error ? 'Informe pelo menos um CFOP de saída (5, 6 ou 7)' : undefined}
            ajuda="Outro CFOP do fornecedor fica sem regra, para você conferir."
          >
            {(a) => (
              <CampoListaChips
                {...a}
                aria-label="CFOPs do fornecedor"
                valores={field.value}
                aoMudar={field.onChange}
                interpretar={interpretarCfop}
                mono
                placeholder="5949, 6949"
              />
            )}
          </Campo>
        )}
      />
    </div>
  );
}

/** "Final fixo" ou "o mesmo do fornecedor"; voltar para o final recupera o que estava digitado. */
function CfopEntrada({ form }: { form: Form }) {
  const cfop = useWatch({ control: form.control, name: 'cfopEntrada' });
  const ultimoFinal = useRef<CfopEntradaPersonalizada>(
    cfop.tipo === 'FINAL' ? cfop : { tipo: 'FINAL', final: '', cfopsFornecedor: [] },
  );
  return (
    <div className="grid gap-3">
      <RotuloVisual>CFOP de entrada</RotuloVisual>
      <Segmentado
        rotulo="CFOP de entrada"
        opcoes={MODOS_CFOP}
        valor={cfop.tipo}
        aoMudar={(tipo) => {
          if (cfop.tipo === 'FINAL') ultimoFinal.current = cfop;
          form.setValue('cfopEntrada', tipo === 'MESMO' ? { tipo } : ultimoFinal.current);
        }}
      />
      {cfop.tipo === 'FINAL' && <CamposFinal control={form.control} />}
    </div>
  );
}

const OPCOES_GRUPO = GRUPOS.map((g) => ({
  valor: g,
  rotulo: GRUPO[g].opcao,
  descricao: GRUPO[g].descricao,
}));

/** A frase ao vivo: o que a classificação vai fazer, enquanto se preenche. */
function Previa({ form }: { form: Form }) {
  const [cfopEntrada, grupo, patrimonio, ativa] = useWatch({
    control: form.control,
    name: ['cfopEntrada', 'grupo', 'patrimonio', 'ativa'],
  });
  return (
    <div className="grid gap-1 rounded-controle border border-borda bg-superficie px-3 py-2">
      <span className="text-xs font-medium text-texto-fraco">Como fica</span>
      <FraseClassificacao
        classificacao={{
          cfopEntrada,
          grupo,
          patrimonio: patrimonio ?? false,
          ativa: ativa ?? true,
        }}
      />
    </div>
  );
}

/** Editor no lugar da linha: nome, CFOP de entrada, grupo contábil, patrimônio e a frase ao vivo. */
export function EditorClassificacao(props: Props) {
  const { form, salvando, enviar } = useEditor(props);
  const erroNome = form.formState.errors.nome?.message;
  return (
    <form
      noValidate
      aria-label={props.rascunho.id ? 'Editar classificação' : 'Nova classificação'}
      className="grid gap-5"
      onSubmit={(e) => void enviar(e)}
    >
      <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
        <Campo
          rotulo="Nome"
          erro={erroNome === undefined ? undefined : erroNome || 'Dê um nome à classificação'}
        >
          {(a) => <Input {...a} maxLength={60} {...form.register('nome')} />}
        </Campo>
        <Controller
          control={form.control}
          name="ativa"
          render={({ field }) => (
            <Interruptor rotulo="Ativa" ligado={field.value ?? true} aoMudar={field.onChange} />
          )}
        />
      </div>
      <CfopEntrada form={form} />
      <Controller
        control={form.control}
        name="grupo"
        render={({ field }) => (
          <div className="grid gap-1.5">
            <RotuloVisual>Grupo contábil</RotuloVisual>
            <Segmentado
              rotulo="Grupo contábil"
              opcoes={OPCOES_GRUPO}
              valor={field.value}
              aoMudar={field.onChange}
            />
          </div>
        )}
      />
      <Controller
        control={form.control}
        name="patrimonio"
        render={({ field }) => (
          <Interruptor
            rotulo="Entra no patrimônio"
            descricao="Itens com esta classificação vão para o controle patrimonial."
            ligado={field.value ?? false}
            aoMudar={field.onChange}
          />
        )}
      />
      <Previa form={form} />
      <div className="flex flex-wrap justify-end gap-2">
        <Button variante="fantasma" onClick={props.aoFechar}>
          Cancelar
        </Button>
        <Button type="submit" variante="primario" carregando={salvando}>
          Salvar classificação
        </Button>
      </div>
    </form>
  );
}
